// Pannes : en cours d'abord (arrêts de production en tête), filtres par état et par type.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Grid, InputAdornment, MenuItem, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import {
  AddOutlined, BarChartOutlined, BuildCircleOutlined, CalendarMonthOutlined, ReportProblemOutlined, PaymentsOutlined, SearchOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { panneAPI } from '../../services/api';
import { apiError, dateHeureFr, fcfa } from '../../utils/format';
import { tokens } from '../../theme';
import { CATEGORIES, GRAVITES, STATUTS, CategorieIcon } from './common';

const Pannes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [d, setD] = useState(null);
  const [statut, setStatut] = useState(location.state?.q ? '' : 'OUVERTES');
  const [categorie, setCategorie] = useState('');
  const [q, setQ] = useState(location.state?.q || '');
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      panneAPI.list({ statut: statut || undefined, categorie: categorie || undefined, q: q || undefined })
        .then((r) => setD(r.data)).catch((e) => setError(apiError(e)));
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [statut, categorie, q]);

  const c = d?.compteurs;
  return (
    <Layout title="Pannes" plain
      actions={(
        <>
          <Button startIcon={<BarChartOutlined />} onClick={() => navigate('/pannes/couts')}>Coûts par équipement</Button>
          {d?.declarant && <Button variant="contained" color="error" startIcon={<AddOutlined />} onClick={() => navigate('/pannes/nouvelle')}>Déclarer une panne</Button>}
        </>
      )}>
      {location.state?.info && <Alert severity="success" sx={{ mb: 2 }}>{location.state.info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {c && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}><Kpi compact icon={ReportProblemOutlined} tint="red" label="Production arrêtée" value={c.arrets} valueColor={c.arrets ? '#B4232A' : undefined} sub="Pannes graves en cours" onClick={() => setStatut('OUVERTES')} /></Grid>
          <Grid item xs={6} md={3}><Kpi compact icon={BuildCircleOutlined} tint="amber" label="En cours" value={c.en_cours} sub="Déclarées ou en réparation" onClick={() => setStatut('OUVERTES')} /></Grid>
          <Grid item xs={6} md={3}><Kpi compact icon={CalendarMonthOutlined} tint="navy" label="Ce mois-ci" value={c.ce_mois} sub="Pannes déclarées" onClick={() => setStatut('')} /></Grid>
          <Grid item xs={6} md={3}><Kpi compact icon={PaymentsOutlined} tint="gray" label="Impact du mois" value={fcfa(c.impact_mois)} valueSize={22}
            sub={`F : réparations ${fcfa(c.cout_mois)} + pertes ${fcfa(c.perte_mois)}`} onClick={() => navigate('/pannes/couts')} /></Grid>
        </Grid>
      )}
      <Panel flush>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, borderBottom: '1px solid', borderColor: 'divider', pr: 1.5 }}>
          <Tabs value={statut} onChange={(e, v) => setStatut(v)} variant="scrollable" sx={{ px: 1, flex: '1 1 auto' }}>
            <Tab value="OUVERTES" label="En cours" /><Tab value="DECLAREE" label="Déclarées" /><Tab value="EN_REPARATION" label="En réparation" />
            <Tab value="RESOLUE" label="Réparées" /><Tab value="" label="Toutes" />
          </Tabs>
          <TextField select size="small" label="Type" value={categorie} onChange={(e) => setCategorie(e.target.value)} sx={{ minWidth: 150, my: 1, ml: 1 }}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
            <MenuItem value="">Tous les types</MenuItem>
            {Object.entries(CATEGORIES).map(([k, o]) => <MenuItem key={k} value={k}>{o.label}</MenuItem>)}
          </TextField>
          <TextField size="small" placeholder="Équipement, n°…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 180, my: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>N°</TableCell><TableCell>Équipement</TableCell><TableCell>Survenue le</TableCell><TableCell>Gravité</TableCell>
                <TableCell>Immobilisation</TableCell><TableCell>Intervenant</TableCell><TableCell align="right">Impact (F)</TableCell><TableCell>État</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d?.pannes.map((p) => {
                const g = GRAVITES[p.gravite];
                return (
                  <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/pannes/${p.id}`)}>
                    <TableCell sx={{ fontFamily: tokens.mono, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.numero}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategorieIcon categorie={p.categorie} sx={{ fontSize: 20, color: tokens.steel }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{p.equipement}</Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{CATEGORIES[p.categorie]?.label}{p.lieu ? ` · ${p.lieu}` : ''}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateHeureFr(p.survenue_le)}</TableCell>
                    <TableCell><Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: g.bg, color: g.fg, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>{g.court}</Box></TableCell>
                    <TableCell sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap', color: ['DECLAREE', 'EN_REPARATION'].includes(p.statut) ? '#B4232A' : undefined }}>{p.immobilisation || '—'}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{p.intervenant || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{p.impact_total === null ? '—' : fcfa(p.impact_total)}</TableCell>
                    <TableCell><Chip size="small" label={STATUTS[p.statut].label} color={STATUTS[p.statut].color} variant={p.statut === 'RESOLUE' ? 'outlined' : 'filled'} /></TableCell>
                  </TableRow>
                );
              })}
              {d && !d.pannes.length && (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  {statut === 'OUVERTES' ? 'Aucune panne en cours.' : 'Aucune panne.'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
    </Layout>
  );
};

export default Pannes;
